import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import ScrollToTop from './components/ScrollToTop';
import RouteAnalytics from './components/RouteAnalytics';
import { HelmetProvider } from "react-helmet-async";
import AuthProvider from './AuthContext';
import ProtectedRoute from './views/Login/ProtectedRoute';
import { EntitledRoute } from './views/Login/EntitledRoute';
import { lazy, Suspense, useEffect } from 'react';
import { Home } from './views/Home/Home';
import { ResetPassword } from './views/Login/ResetPassword';
import { applyStoredConsent } from './lib/consent';

const Stadium = lazy(() => import('./views/Stadium/Stadium').then(m => ({ default: m.Stadium })))
const Contact = lazy(() => import('./views/Contact/Contact').then(m => ({ default: m.Contact })))
const ProductDetails = lazy(() => import('./views/ProductDetails/ProductDetails').then(m => ({ default: m.ProductDetails })))
const Retractable = lazy(() => import('./views/Retractable/Retractable').then(m => ({ default: m.Retractable })))
const Bleachers = lazy(() => import('./views/Bleachers/Bleachers').then(m => ({ default: m.Bleachers })))
const Flooring = lazy(() => import('./views/Flooring/Flooring').then(m => ({ default: m.Flooring })))
const FlooringDetails = lazy(() => import('./views/Flooring/FlooringDetails').then(m => ({ default: m.FlooringDetails })))
const Equipment = lazy(() => import('./views/Equipment/Equipment').then(m => ({ default: m.Equipment })))
const EquipmentDetails = lazy(() => import('./views/Equipment/EquipmentDetails').then(m => ({ default: m.EquipmentDetails })))
const JobRefList = lazy(() => import('./views/JobRef/JobRefList').then(m => ({ default: m.JobRefList })))
const JobRef = lazy(() => import('./views/JobRef/JobRef').then(m => ({ default: m.JobRef })))
const Industries = lazy(() => import('./views/Industries/Industries').then(m => ({ default: m.Industries })))
const StadiumIndustry = lazy(() => import('./views/Industries/Stadium').then(m => ({ default: m.Stadium })))
const AuditoriumIndustry = lazy(() => import('./views/Industries/Auditorium').then(m => ({ default: m.Auditorium })))
const SchoolIndustry = lazy(() => import('./views/Industries/School').then(m => ({ default: m.School })))
const CorporateIndustry = lazy(() => import('./views/Industries/Corporate').then(m => ({ default: m.Corporate })))
const GovernmentIndustry = lazy(() => import('./views/Industries/Government').then(m => ({ default: m.Government })))
const Architects = lazy(() => import('./views/Architects/Architects').then(m => ({ default: m.Architects })))
const Login = lazy(() => import('./views/Login/Login').then(m => ({ default: m.Login })))
const Email = lazy(() => import('./views/Disclosures/Email').then(m => ({ default: m.Email })))
const Unsubscribe = lazy(() => import('./views/Unsubscribe/Unsubscribe').then(m => ({ default: m.Unsubscribe })))

const Blog = lazy(() => import('./views/BlogView').then(m => ({ default: m.Blog })))
const BlogPost = lazy(() => import('./views/BlogView').then(m => ({ default: m.BlogPost })))
const CategoryView = lazy(() => import('./views/BlogView').then(m => ({ default: m.CategoryView })))

const Clients = lazy(() => import('./views/Clients/Clients'))
const ClientDetails = lazy(() => import('./views/Clients/ClientDetails'))

const SeatingDirectory = lazy(() => import('./views/SeatingDirectory/SeatingDirectory'))
const SeatingDirectoryDetails = lazy(() => import('./views/SeatingDirectory/SeatingDirectoryDetails'))

const BlogDraftList = lazy(() => import('./views/BlogView/BlogDraftList').then(m => ({ default: m.BlogDraftList })))
const BlogBuilder = lazy(() => import('./views/BlogView/BlogBuilder').then(m => ({ default: m.BlogBuilder })))
const BlogDraftPost = lazy(() => import('./views/BlogView/BlogDraftPost').then(m => ({ default: m.BlogDraftPost })))

const PrivacyPolicy = lazy(() => import('./views/Legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const TermsAndConditions = lazy(() => import('./views/Legal/TermsAndConditions').then(m => ({ default: m.TermsAndConditions })))

const Sitemap = lazy(() => import('./views/Sitemap/Sitemap').then(m => ({ default: m.Sitemap })))
const InTheNews = lazy(() => import('./views/InTheNews/InTheNews').then(m => ({ default: m.InTheNews })))
const GenerateSitemap = lazy(() => import('./views/Sitemap/GenerateSitemap').then(m => ({ default: m.GenerateSitemap })))

const Careers = lazy(() => import('./views/Jobs/Careers').then(m => ({ default: m.Careers })))
const ResourceCentre = lazy(() => import('./views/ResourceCentre/ResourceCentre').then(m => ({ default: m.ResourceCentre })))
const JobPost = lazy(() => import('./views/Jobs/JobPost').then(m => ({ default: m.JobPost })))
const JobApplications = lazy(() => import('./views/Jobs/JobApplications'))

const NotFound = lazy(() => import('./views/NotFound/NotFound').then(m => ({ default: m.NotFound })))

const Dashboard = lazy(() => import('./views/Dashboard/Dashboard').then(m => ({ default: m.Dashboard })))
const Analytics = lazy(() => import('./views/Analytics/Analytics').then(m => ({ default: m.Analytics })))
const Inquiries = lazy(() => import('./views/Inquiries/Inquiries').then(m => ({ default: m.Inquiries })))
const Newsletter = lazy(() => import('./views/Newsletter/Newsletter').then(m => ({ default: m.Newsletter })))
const ColdEmail = lazy(() => import('./views/ColdEmail/ColdEmail').then(m => ({ default: m.ColdEmail })))
const Resources = lazy(() => import('./views/Login/Content/Content').then(m => ({ default: m.Content })))
const Users = lazy(() => import('./views/Users/Users').then(m => ({ default: m.Users })))
const Performance = lazy(() => import('./views/Performance/Performance').then(m => ({ default: m.Performance })))
const MyReview = lazy(() => import('./views/Performance/MyReview').then(m => ({ default: m.MyReview })))
const TeamReviews = lazy(() => import('./views/Performance/TeamReviews').then(m => ({ default: m.TeamReviews })))
const ReviewDetail = lazy(() => import('./views/Performance/ReviewDetail').then(m => ({ default: m.ReviewDetail })))
const PerformanceAdmin = lazy(() => import('./views/Performance/PerformanceAdmin').then(m => ({ default: m.PerformanceAdmin })))

const SeatEstimator = lazy(() => import('./views/SeatEstimator/SeatEstimator').then(m => ({ default: m.SeatEstimator })))

const PublicLayout = lazy(() => import('./views/AdminLayout/PublicLayout').then(m => ({ default: m.PublicLayout })))
const AdminLayout = lazy(() => import('./views/AdminLayout/AdminLayout').then(m => ({ default: m.AdminLayout })))

function App() {
  useEffect(() => {
    applyStoredConsent()
  }, [])

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <Router>
            <ScrollToTop />
            <RouteAnalytics />
            <Suspense fallback={<div className="px-8 md:px-24 py-8 animate-pulse">
              <div className="h-8 w-1/3 bg-gray-200 rounded mb-4" />
              <div className="h-4 w-full bg-gray-200 rounded mb-2" />
              <div className="h-4 w-2/3 bg-gray-200 rounded" />
            </div>}>
              <Routes>
                {/* PUBLIC: marketing site — Navbar + Footer + CookieConsent. Redirects signed-in users to admin. */}
                <Route element={<PublicLayout />}>
                  <Route path='/' element={<Home />} />
                  <Route path='/careers' element={<Careers />} />
                  <Route path='/careers/:jobId' element={<JobPost />} />
                  <Route path='/retractable-seating' element={<Retractable />} />
                  <Route path='/retractable-seating/:stadiumId' element={<ProductDetails />} />
                  <Route path='/stadium-seating' element={<Stadium />} />
                  <Route path='/stadium-seating/:stadiumId' element={<ProductDetails />} />
                  <Route path='/bleachers' element={<Bleachers />} />
                  <Route path='/bleacher-seating/:stadiumId' element={<ProductDetails />} />
                  <Route path='/sports-flooring' element={<Flooring />} />
                  <Route path='/sports-flooring/:systemId' element={<FlooringDetails />} />
                  <Route path='/facility-equipment' element={<Equipment />} />
                  <Route path='/facility-equipment/:equipmentId' element={<EquipmentDetails />} />
                  <Route path='/job-references' element={<JobRefList />} />
                  <Route path='/job-references/:jobId' element={<JobRef />} />
                  <Route path='/industries' element={<Industries />} />
                  <Route path='/industries/stadium' element={<StadiumIndustry />} />
                  <Route path='/industries/auditorium' element={<AuditoriumIndustry />} />
                  <Route path='/industries/school' element={<SchoolIndustry />} />
                  <Route path='/industries/corporate' element={<CorporateIndustry />} />
                  <Route path='/industries/government' element={<GovernmentIndustry />} />
                  <Route path='/architects' element={<Architects />} />
                  <Route path='/contact' element={<Contact />} />
                  <Route path='/login' element={<Login />} />
                  <Route path='/reset-password' element={<ResetPassword />} />
                  <Route path='/email-disclosure' element={<Email />} />
                  <Route path='/unsubscribe' element={<Unsubscribe />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/c/:categoryId" element={<CategoryView />} />
                  <Route path="/blog/b/:blogId" element={<BlogPost />} />
                  <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                  <Route path='/terms-and-conditions' element={<TermsAndConditions />} />
                  <Route path='/sitemap' element={<Sitemap />} />
                  <Route path='/in-the-news' element={<InTheNews />} />
                  <Route path='/resource-centre' element={<ResourceCentre />} />
                  {/* US spelling lands on the same hub rather than a 404. */}
                  <Route path='/resource-center' element={<Navigate to='/resource-centre' replace />} />
                  <Route path='*' element={<NotFound />} />
                </Route>

                {/* PROTECTED: internal app — Sidebar only, no Navbar/Footer/Consent. */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<AdminLayout />}>
                    {/* Available to any signed-in user */}
                    <Route path='/dashboard' element={<Dashboard />} />
                    <Route path='/generate-sitemap' element={<GenerateSitemap />} />
                    <Route path='/resources' element={<Resources />} />
                    <Route path='/seat-estimator' element={<SeatEstimator />} />
                    <Route path='/users' element={<Users />} />

                    {/* Reviews are one destination with tabs inside it, so the
                        whole portal nests under a single shell. Own review and
                        team reviews need no flag — RLS scopes them to you and
                        your direct reports, with the reporting chain on the
                        Users page as the source of truth for who reviews whom.
                        Admin is the exception, gated below. */}
                    <Route path='/performance' element={<Performance />}>
                      <Route index element={<MyReview />} />
                      <Route path='team' element={<TeamReviews />} />
                      <Route path='r/:reviewId' element={<ReviewDetail />} />

                      {/* Stage 3 — HR, Finance, and the directors. The only
                          place compensation is readable. */}
                      <Route element={<EntitledRoute requires='isViewPerformance' />}>
                        <Route path='admin' element={<PerformanceAdmin />} />
                      </Route>
                    </Route>

                    {/* Entitlement-gated — redirects to /dashboard if user lacks permission */}
                    <Route element={<EntitledRoute requires='isViewClients' />}>
                      <Route path='/clients' element={<Clients />} />
                      <Route path='/clients/:id' element={<ClientDetails />} />
                      <Route path='/inquiries' element={<Inquiries />} />
                      <Route path='/seating-directory' element={<SeatingDirectory />} />
                      <Route path='/seating-directory/:chairId' element={<SeatingDirectoryDetails />} />
                    </Route>

                    <Route element={<EntitledRoute requires='isViewBlogBuilder' />}>
                      <Route path='/blogDrafts' element={<BlogDraftList />} />
                      <Route path='/blogDrafts/add' element={<BlogBuilder />} />
                      <Route path='/blogDrafts/:id' element={<BlogDraftPost />} />
                    </Route>

                    <Route element={<EntitledRoute requires='isViewJobs' />}>
                      <Route path='/jobs' element={<JobApplications />} />
                    </Route>

                    <Route element={<EntitledRoute requires='isViewDashboard' />}>
                      <Route path='/analytics' element={<Analytics />} />
                    </Route>

                    <Route element={<EntitledRoute requires='isViewNewsletter' />}>
                      <Route path='/newsletter' element={<Newsletter />} />
                    </Route>

                    {/* Separate from isViewNewsletter so a salesperson can run
                        outreach without access to the subscriber list. */}
                    <Route element={<EntitledRoute requires='isViewColdEmail' />}>
                      <Route path='/cold-email' element={<ColdEmail />} />
                    </Route>
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </Router>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
